import { getCategoryGuidance } from '../../models/category-guidance.mjs';
import {
  createListingDraftFromFirstPhoto,
  updateListingDraft,
} from '../../models/listing-draft.mjs';
import { normalizePriceCurrency } from '../../utils/price-input.mjs';
import { formatListingPrice } from '../../utils/rendering.mjs';
import {
  getFashionAttributes,
  isFashionCategory,
  updateFashionAttributes,
} from '../../utils/fashion-attributes.mjs';

export const MAX_PRICE_CDF = 2_147_483_647;

const promptLabels = {
  accessoires: 'Accessoires',
  avant: 'Avant',
  arriere: 'Arrière',
  back: 'Dos',
  carte_visite_logo: 'Carte de visite ou logo',
  chambre: 'Chambre',
  cote: 'Vue latérale',
  cuisine: 'Cuisine',
  defaut: 'Défaut visible',
  detail: 'Détail',
  detail_materiel: 'Détail du matériel',
  droite: 'Vue droite',
  emballage_etiquette: 'Emballage ou étiquette',
  etiquette: 'Étiquette',
  etat_materiel: 'État du matériel',
  face: 'Face',
  facade: 'Façade',
  gauche: 'Vue gauche',
  interieur: 'Intérieur',
  kilometrage: 'Kilométrage',
  lot_complet: 'Lot complet',
  logo_entreprise: "Logo ou visuel de l'entreprise",
  salle_de_bain: 'Salle de bain',
  salon: 'Salon',
  screen_on: 'Écran allumé',
  tableau_de_bord: 'Tableau de bord',
  taille: 'Taille',
  vue_ensemble: "Vue d'ensemble",
};

const conditionRequiredCategories = new Set([
  'phones_tablets',
  'vehicles',
  'fashion',
  'home_garden',
]);

function hasPromptPhoto(draft, promptId) {
  return draft.photos.some(
    (photo) => photo.promptId === promptId && isPhotoReadyForDraft(photo),
  );
}

function isVehicleCategory(draft) {
  return draft.details.categoryId === 'vehicles';
}

function buildPrompt(promptId, required, draft) {
  const photo = draft.photos.find((entry) => entry.promptId === promptId);

  return {
    id: promptId,
    label: promptLabels[promptId] ?? promptId,
    publishRequired: required && isVehicleCategory(draft),
    required,
    completed: hasPromptPhoto(draft, promptId),
    previewUrl: resolvePhotoRenderUrl(photo),
    uploadError: photo?.uploadError ?? '',
    uploadStatus: photo?.uploadStatus ?? '',
  };
}

function resolveGuidance(categoryId) {
  return getCategoryGuidance(categoryId);
}

function createPhotoId(prefix) {
  return globalThis.crypto?.randomUUID?.()
    ? `${prefix}-${globalThis.crypto.randomUUID()}`
    : `${prefix}-${Date.now()}`;
}

function resolveSourcePresetId(kind, promptId) {
  if (kind === 'guided') {
    return promptId || 'guided';
  }

  return 'capture';
}

function resolvePhotoRenderUrl(photo) {
  return photo?.publicUrl || photo?.url || photo?.previewUrl || '';
}

function isPhotoReadyForDraft(photo) {
  if (!photo) {
    return false;
  }

  if (photo.uploadStatus === 'uploaded') {
    return true;
  }

  if (photo.uploadStatus === 'uploading' || photo.uploadStatus === 'failed') {
    return false;
  }

  return Boolean(resolvePhotoRenderUrl(photo));
}

function resolvePrimaryPhoto(draft) {
  return draft.photos.find((photo) => photo.kind === 'primary') ?? draft.photos[0] ?? null;
}

function hasCompleteDraftPatch(patch = {}) {
  return Boolean(
    typeof patch.title === 'string' &&
      patch.title.trim() &&
      typeof patch.categoryId === 'string' &&
      patch.categoryId.trim() &&
      typeof patch.condition === 'string' &&
      patch.condition.trim() &&
      typeof patch.description === 'string' &&
      patch.description.trim(),
  );
}

function createSelectedPhotoRecord(
  file,
  {
    createPreviewUrl,
    kind,
    promptId = '',
  },
) {
  const sourcePresetId = resolveSourcePresetId(kind, promptId);
  const fileName = file.name || `${sourcePresetId}-${Date.now()}.jpg`;
  const contentType = file.type || 'image/jpeg';
  const previewUrl =
    typeof createPreviewUrl === 'function'
      ? createPreviewUrl(file)
      : file.previewUrl || file.url || '';

  return {
    id: createPhotoId(sourcePresetId),
    kind,
    promptId,
    sourcePresetId,
    uploadStatus: 'uploading',
    uploadError: '',
    previewUrl,
    url: previewUrl,
    fileName,
    contentType,
    sizeBytes: file.size ?? file.sizeBytes ?? null,
  };
}

async function readSelectedPhotoBytes(file) {
  if (typeof file.arrayBuffer === 'function') {
    return new Uint8Array(await file.arrayBuffer());
  }

  if (file.bytes instanceof Uint8Array) {
    return file.bytes;
  }

  if (Array.isArray(file.bytes)) {
    return Uint8Array.from(file.bytes);
  }

  throw new Error('Photo introuvable.');
}

async function uploadDraftPhoto({
  mediaService,
  file,
  photo,
  upload = null,
}) {
  const bytes = upload?.bytes ?? await readSelectedPhotoBytes(file);
  const contentType = upload?.contentType ?? photo.contentType ?? 'image/jpeg';
  const fileName = upload?.fileName ?? photo.fileName ?? `${photo.sourcePresetId || photo.id}.jpg`;
  const slot = await mediaService.requestUploadSlot({
    contentType,
    fileName,
    sourcePresetId: photo.sourcePresetId || resolveSourcePresetId(photo.kind, photo.promptId),
  });

  await mediaService.uploadBytes({
    bytes,
    contentType,
    publicUrl: slot.publicUrl,
    uploadUrl: slot.uploadUrl,
  });

  return {
    ...photo,
    objectKey: slot.objectKey,
    photoId: slot.photoId,
    publicUrl: slot.publicUrl,
    sourcePresetId: slot.sourcePresetId,
    uploadStatus: 'uploaded',
    uploadError: '',
    url: slot.publicUrl,
  };
}

function attachDraftToError(error, draft) {
  const nextError = error instanceof Error ? error : new Error(String(error || 'Erreur upload.'));

  nextError.draft = draft;
  return nextError;
}

export function buildStoryShareText({ listingUrl, title }) {
  return `Je vends sur Zwibba ! ${title || 'Mon annonce'} — ${listingUrl}`;
}

export function canShareStoryImage({
  blobCtor = globalThis.Blob,
  fileCtor = globalThis.File,
  navigatorObject = globalThis.navigator,
} = {}) {
  if (!navigatorObject?.share || !navigatorObject?.canShare || !fileCtor || !blobCtor) {
    return false;
  }

  try {
    const file = new fileCtor([new blobCtor([])], 'zwibba-story.png', {
      type: 'image/png',
    });

    return navigatorObject.canShare({ files: [file] });
  } catch {
    return false;
  }
}

export async function shareStoryImageNative({
  fetchFn = globalThis.fetch,
  fileCtor = globalThis.File,
  imageUrl,
  listingUrl,
  navigatorObject = globalThis.navigator,
  storyImageUrl,
  title,
}) {
  const effectiveImageUrl = storyImageUrl || imageUrl;

  if (!effectiveImageUrl || !navigatorObject?.share || !fileCtor) {
    throw new Error('Partage natif indisponible.');
  }

  const response = await fetchFn(effectiveImageUrl);
  const blob = await response.blob();
  const file = new fileCtor([blob], 'zwibba-story.png', {
    type: blob.type || 'image/png',
  });
  const absoluteUrl =
    typeof window === 'undefined'
      ? listingUrl
      : new URL(listingUrl, window.location.origin).toString();

  await navigatorObject.share({
    files: [file],
    text: buildStoryShareText({ listingUrl: absoluteUrl, title }),
    title: 'Je vends sur Zwibba !',
    url: absoluteUrl,
  });
}

export function getGuidedPhotoPrompts(draft) {
  const guidance = resolveGuidance(draft.details.categoryId);

  return [
    ...guidance.required.map((promptId) => buildPrompt(promptId, true, draft)),
    ...guidance.optional.map((promptId) => buildPrompt(promptId, false, draft)),
  ];
}

export function getMissingRequiredPhotoPrompts(draft) {
  return getGuidedPhotoPrompts(draft).filter((prompt) => prompt.required && !prompt.completed);
}

export function isConditionRequired(draft) {
  return conditionRequiredCategories.has(draft.details.categoryId);
}

export function addGuidedPhotoToDraft(
  draft,
  {
    promptId,
    photo,
    now = new Date().toISOString(),
  },
) {
  const nextPhoto = {
    ...photo,
    id: photo.id ?? `photo-${draft.photos.length + 1}`,
    kind: 'guided',
    promptId,
    url: photo.url ?? photo.previewUrl ?? '',
    previewUrl: photo.previewUrl ?? photo.url ?? '',
  };
  const otherPhotos = draft.photos.filter((existingPhoto) => existingPhoto.promptId !== promptId);
  const primaryPhotos = otherPhotos.filter((existingPhoto) => existingPhoto.kind === 'primary');
  const guidedPhotos = otherPhotos.filter((existingPhoto) => existingPhoto.kind !== 'primary');

  return updateListingDraft(
    draft,
    {
      photos: [...primaryPhotos, ...guidedPhotos, nextPhoto],
      guidance: resolveGuidance(draft.details.categoryId),
    },
    { now },
  );
}

export function applyAiResultToDraft(
  draft,
  aiResult,
  { now = new Date().toISOString() } = {},
) {
  if (aiResult.status === 'manual_fallback' || !hasCompleteDraftPatch(aiResult.draftPatch ?? {})) {
    return updateListingDraft(
      draft,
      {
        ai: {
          applied: false,
          status: 'manual_fallback',
          message: aiResult.message || "L'IA n'a pas pu préparer ce brouillon. Continuez manuellement.",
        },
      },
      { now },
    );
  }

  const patch = aiResult.draftPatch ?? {};
  const nextCategoryId = patch.categoryId ?? draft.details.categoryId;
  const nextAttributesJson = isFashionCategory(nextCategoryId)
    ? updateFashionAttributes(draft.details.attributesJson, {
        itemType: patch.itemType,
        size: patch.size,
      })
    : updateFashionAttributes(draft.details.attributesJson);

  return updateListingDraft(
    draft,
    {
      details: {
        title: patch.title ?? draft.details.title,
        categoryId: nextCategoryId,
        condition: patch.condition ?? draft.details.condition,
        attributesJson: nextAttributesJson,
        description: patch.description ?? draft.details.description,
      },
      ai: {
        applied: true,
        status: 'ready',
        message: 'À partir de votre photo.',
      },
      guidance: resolveGuidance(nextCategoryId),
    },
    { now },
  );
}

export function validateDraftForPublish(
  draft,
  {
    uploadsBusy = false,
  } = {},
) {
  const errors = [];
  const primaryPhoto = resolvePrimaryPhoto(draft);
  const priceCurrency = normalizePriceCurrency(draft.details.priceCurrency);
  const priceAmount = draft.details.priceAmount;

  if (uploadsBusy) {
    errors.push({
      field: 'uploads_pending',
      message: 'Attendez la fin des téléversements avant de publier.',
    });
  }

  if (!isPhotoReadyForDraft(primaryPhoto)) {
    errors.push({
      field: 'photo',
      message:
        primaryPhoto?.uploadStatus === 'uploading'
          ? 'Attendez la fin du téléversement de la photo principale.'
          : primaryPhoto?.uploadStatus === 'failed'
            ? 'Réessayez la photo principale avant de publier.'
            : 'Ajoutez au moins une photo.',
    });
  }

  if (isVehicleCategory(draft)) {
    const missingVehiclePrompts = getMissingRequiredPhotoPrompts(draft);

    if (missingVehiclePrompts.length) {
      errors.push({
        field: 'guided_photos',
        message: `Ajoutez ces vues avant de publier : ${missingVehiclePrompts
          .map((prompt) => prompt.label)
          .join(', ')}.`,
      });
    }
  }

  if (!draft.details.categoryId) {
    errors.push({
      field: 'category',
      message: 'Confirmez la catégorie.',
    });
  }

  if (isConditionRequired(draft) && !draft.details.condition) {
    errors.push({
      field: 'condition',
      message: "L'état est requis pour cette catégorie.",
    });
  }

  if (isFashionCategory(draft.details.categoryId)) {
    const fashionAttributes = getFashionAttributes(draft.details.attributesJson);

    if (!fashionAttributes.itemType) {
      errors.push({
        field: 'fashion_item_type',
        message: "Choisissez le type d'article pour la rubrique Mode.",
      });
    }

    if (!fashionAttributes.size) {
      errors.push({
        field: 'fashion_size',
        message: 'Choisissez une taille pour la rubrique Mode.',
      });
    }
  }

  if (!priceCurrency) {
    errors.push({
      field: 'price',
      message: 'Choisissez une devise pour votre prix.',
    });
  } else if (priceAmount === null || priceAmount === undefined) {
    errors.push({
      field: 'price',
      message: 'Choisissez un prix final.',
    });
  } else if (priceAmount > MAX_PRICE_CDF) {
    errors.push({
      field: 'price',
      message: `Le prix final doit rester inférieur à ${formatListingPrice({
        priceAmount: MAX_PRICE_CDF,
        priceCurrency,
      })}.`,
    });
  }

  if (!draft.details.description?.trim()) {
    errors.push({
      field: 'description',
      message: 'Ajoutez une courte description.',
    });
  }

  if (!draft.details.area?.trim()) {
    errors.push({
      field: 'area',
      message: 'Définissez votre zone dans le profil avant de publier.',
    });
  }

  return errors;
}

export function refreshReviewValidationErrors(
  currentErrors,
  draft,
  {
    uploadsBusy = false,
  } = {},
) {
  if (!draft || !Array.isArray(currentErrors) || currentErrors.length === 0) {
    return Array.isArray(currentErrors) ? currentErrors : [];
  }

  return validateDraftForPublish(draft, {
    uploadsBusy,
  });
}

export function decidePublishGate({
  draft,
  session,
  uploadsBusy = false,
}) {
  const validationErrors = validateDraftForPublish(draft, {
    uploadsBusy,
  });

  if (validationErrors.length) {
    return {
      status: 'validation_error',
      nextRoute: '#review',
      errors: validationErrors,
    };
  }

  if (!session) {
    return {
      status: 'needs_auth',
      nextRoute: '#auth-welcome',
    };
  }

  return {
    status: 'ready_for_submission',
    nextRoute: '#publish',
  };
}

export function createReadyDraft(overrides = {}) {
  const now = overrides.now ?? new Date().toISOString();
  const condition =
    Object.prototype.hasOwnProperty.call(overrides, 'condition') ? overrides.condition : '';
  const categoryId = overrides.categoryId ?? 'electronics';
  const hasExplicitPriceAmount = Object.prototype.hasOwnProperty.call(overrides, 'priceAmount');
  const hasExplicitPriceCurrency = Object.prototype.hasOwnProperty.call(overrides, 'priceCurrency');
  const hasLegacyPrice = Object.prototype.hasOwnProperty.call(overrides, 'priceCdf');
  const defaultPreviewUrl = overrides.photoUrl ?? '/uploads/phone-front.jpg';
  const photos =
    Object.prototype.hasOwnProperty.call(overrides, 'photos')
      ? overrides.photos
      : [
          {
            id: 'photo-1',
            kind: 'primary',
            url: defaultPreviewUrl,
            previewUrl: defaultPreviewUrl,
            uploadStatus: 'uploaded',
          },
        ];
  const draft = createListingDraftFromFirstPhoto({
    photoUrl: defaultPreviewUrl,
    photo: {
      id: 'photo-1',
      previewUrl: defaultPreviewUrl,
      sizeBytes: 1_200_000,
    },
    now,
  });

  return updateListingDraft(
    draft,
    {
      photos,
      details: {
        title: overrides.title ?? 'Annonce prête à publier',
        categoryId,
        condition,
        attributesJson: overrides.attributesJson ?? null,
        priceAmount: hasExplicitPriceAmount
          ? overrides.priceAmount
          : hasLegacyPrice
            ? overrides.priceCdf
            : 450_000,
        priceCurrency: hasExplicitPriceCurrency
          ? overrides.priceCurrency
          : hasLegacyPrice && overrides.priceCdf == null
            ? ''
            : 'CDF',
        description: overrides.description ?? 'Article prêt, propre et disponible immédiatement.',
        area: overrides.area ?? 'Golf',
      },
      ai: overrides.ai ?? {
        applied: true,
        status: 'ready',
        message: 'À partir de votre photo.',
      },
      guidance: resolveGuidance(categoryId),
    },
    { now },
  );
}

export function createPostFlowController({
  draftStorage,
  imageCompressionService,
  aiDraftService,
  mediaService,
  createPreviewUrl = (file) => file.previewUrl || file.url || '',
  now = () => new Date().toISOString(),
  onUploadStageChange = () => {},
} = {}) {
  if (!draftStorage || !imageCompressionService || !aiDraftService || !mediaService) {
    throw new Error('Post flow dependencies are required.');
  }

  return {
    async captureFirstPhoto(file) {
      await onUploadStageChange({
        flow: 'primary',
        stage: 'compressing',
      });
      const compressionResult = await imageCompressionService.compressImage(
        file,
        createSelectedPhotoRecord(file, {
          createPreviewUrl,
          kind: 'primary',
        }),
      );
      const preparedPhoto = compressionResult?.photo ?? compressionResult;
      const upload = compressionResult?.upload ?? null;
      let draft = createListingDraftFromFirstPhoto({
        photoUrl: preparedPhoto.previewUrl ?? preparedPhoto.url,
        photo: preparedPhoto,
        now: now(),
      });
      draftStorage.saveDraft(draft);

      try {
        await onUploadStageChange({
          flow: 'primary',
          stage: 'uploading',
        });
        const uploadedPhoto = await uploadDraftPhoto({
          file,
          mediaService,
          photo: draft.photos[0],
          upload,
        });
        draft = updateListingDraft(
          draft,
          {
            photos: [uploadedPhoto],
          },
          { now: now() },
        );
        await onUploadStageChange({
          flow: 'primary',
          stage: 'analyzing',
        });
        const aiResult = await aiDraftService.generateDraft(uploadedPhoto);

        draft = applyAiResultToDraft(draft, aiResult, {
          now: now(),
        });
        draftStorage.saveDraft(draft);

        return draft;
      } catch (error) {
        const failedDraft = updateListingDraft(
          draft,
          {
            photos: [
              {
                ...draft.photos[0],
                uploadStatus: 'failed',
                uploadError:
                  error instanceof Error ? error.message : 'Impossible de téléverser la photo.',
              },
            ],
          },
          { now: now() },
        );
        draftStorage.saveDraft(failedDraft);
        throw attachDraftToError(error, failedDraft);
      } finally {
        await onUploadStageChange(null);
      }
    },
    async replacePrimaryPhoto(file) {
      const draft = draftStorage.loadDraft();

      if (!draft) {
        throw new Error('No draft available.');
      }

      await onUploadStageChange({
        flow: 'primary',
        stage: 'compressing',
      });
      const compressionResult = await imageCompressionService.compressImage(
        file,
        createSelectedPhotoRecord(file, {
          createPreviewUrl,
          kind: 'primary',
        }),
      );
      const preparedPhoto = compressionResult?.photo ?? compressionResult;
      const upload = compressionResult?.upload ?? null;
      const nonPrimaryPhotos = draft.photos.filter((photo) => photo.kind !== 'primary');

      try {
        await onUploadStageChange({
          flow: 'primary',
          stage: 'uploading',
        });
        const uploadedPhoto = await uploadDraftPhoto({
          file,
          mediaService,
          photo: preparedPhoto,
          upload,
        });
        let updatedDraft = updateListingDraft(
          draft,
          {
            photos: [uploadedPhoto, ...nonPrimaryPhotos],
            upload: {
              compressed: Boolean(preparedPhoto.wasCompressed),
              originalSizeBytes: preparedPhoto.originalSizeBytes ?? preparedPhoto.sizeBytes ?? null,
              uploadedSizeBytes: preparedPhoto.sizeBytes ?? null,
            },
          },
          { now: now() },
        );
        draftStorage.saveDraft(updatedDraft);

        await onUploadStageChange({
          flow: 'primary',
          stage: 'analyzing',
        });
        const aiResult = await aiDraftService.generateDraft(uploadedPhoto);

        updatedDraft = applyAiResultToDraft(updatedDraft, aiResult, {
          now: now(),
        });
        draftStorage.saveDraft(updatedDraft);

        return updatedDraft;
      } catch (error) {
        throw attachDraftToError(error, draftStorage.loadDraft() ?? draft);
      } finally {
        await onUploadStageChange(null);
      }
    },
    async addGuidedPhoto(promptId, file) {
      const draft = draftStorage.loadDraft();

      if (!draft) {
        throw new Error('No draft available.');
      }

      await onUploadStageChange({
        flow: 'guided',
        stage: 'compressing',
      });
      const compressionResult = await imageCompressionService.compressImage(
        file,
        createSelectedPhotoRecord(file, {
          createPreviewUrl,
          kind: 'guided',
          promptId,
        }),
      );
      const compressedPhoto = compressionResult?.photo ?? compressionResult;
      const upload = compressionResult?.upload ?? null;
      let updatedDraft = addGuidedPhotoToDraft(draft, {
        promptId,
        photo: compressedPhoto,
        now: now(),
      });

      draftStorage.saveDraft(updatedDraft);

      const insertedPhoto = updatedDraft.photos.find((photo) => photo.promptId === promptId);

      try {
        await onUploadStageChange({
          flow: 'guided',
          stage: 'uploading',
        });
        const uploadedPhoto = await uploadDraftPhoto({
          file,
          mediaService,
          photo: insertedPhoto,
          upload,
        });
        updatedDraft = addGuidedPhotoToDraft(updatedDraft, {
          promptId,
          photo: uploadedPhoto,
          now: now(),
        });
        draftStorage.saveDraft(updatedDraft);

        return updatedDraft;
      } catch (error) {
        const failedDraft = addGuidedPhotoToDraft(updatedDraft, {
          promptId,
          photo: {
            ...insertedPhoto,
            uploadStatus: 'failed',
            uploadError:
              error instanceof Error ? error.message : 'Impossible de téléverser la photo.',
          },
          now: now(),
        });
        draftStorage.saveDraft(failedDraft);
        throw attachDraftToError(error, failedDraft);
      } finally {
        await onUploadStageChange(null);
      }
    },
    saveDraft(draft) {
      return draftStorage.saveDraft(draft);
    },
  };
}
